import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import axios from "axios";
import config from "../config";
import { useDispatch } from "react-redux";
import { setUser } from "../redux/slice/Auth";
import Faq from "../components/Faq";

const COUNTRIES = [
  { code: "+91",  flag: "🇮🇳", label: "India" },
  { code: "+1",   flag: "🇺🇸", label: "USA" },
  { code: "+44",  flag: "🇬🇧", label: "UK" },
  { code: "+971", flag: "🇦🇪", label: "UAE" },
  { code: "+65",  flag: "🇸🇬", label: "Singapore" },
  { code: "+61",  flag: "🇦🇺", label: "Australia" },
];

const EXPERIENCE_OPTIONS = [
  { value: "0",  label: "Less than 1 year" },
  { value: "1",  label: "1 year" },
  { value: "2",  label: "2 years" },
  { value: "3",  label: "3 years" },
  { value: "4",  label: "4 years" },
  { value: "5",  label: "5 years" },
  { value: "7",  label: "6–7 years" },
  { value: "10", label: "10+ years" },
];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://meragym.com/create-fitness-trainer-profile",
      "url": "https://meragym.com/create-fitness-trainer-profile",
      "name": "Create Free Fitness Trainer Profile | Register on MeraGym",
      "description":
        "Create your free fitness trainer profile on MeraGym. List your services, connect with clients across India, and grow your personal training business — no cost, no commission.",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home",             "item": "https://meragym.com" },
          { "@type": "ListItem", "position": 2, "name": "Fitness Trainers", "item": "https://meragym.com/gym-fitness-trainer" },
          { "@type": "ListItem", "position": 3, "name": "Create Fitness Trainer Profile", "item": "https://meragym.com/create-fitness-trainer-profile" },
        ],
      },
    },
    {
      "@type": "Service",
      "name": "Create Free Fitness Trainer Profile",
      "provider": { "@type": "Organization", "name": "MeraGym", "url": "https://meragym.com" },
      "description": "Free fitness trainer profile listing on MeraGym. Reach thousands of clients, manage availability, and grow your personal training business across India.",
      "areaServed": { "@type": "Country", "name": "India" },
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR", "description": "Free profile creation — no commission, no setup fee" },
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How do I create my free fitness trainer profile on MeraGym?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "To create your free fitness trainer profile, click on the 'Create Free Profile' button, enter your basic details like name and email, verify your account via OTP, and your profile will be live instantly."
          }
        },
        {
          "@type": "Question",
          "name": "Is it really free to create a fitness trainer profile?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, it is 100% free. When you create your free fitness trainer profile on MeraGym, there are no hidden charges, registration fees, or setup costs involved."
          }
        },
        {
          "@type": "Question",
          "name": "What information do I need to create my free fitness trainer profile?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You only need your name, email, phone number, city, and years of experience to create your free fitness trainer profile. You can add certifications and photos later."
          }
        },
        {
          "@type": "Question",
          "name": "Will my profile rank on Google after I create my free fitness trainer profile?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, every time you create your free fitness trainer profile on MeraGym, our platform ensures it is SEO-optimized to help you rank higher in local Google search results for fitness trainers."
          }
        },
        {
          "@type": "Question",
          "name": "Are there any commissions after I create my free fitness trainer profile?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, MeraGym is a zero-commission platform. After you create your free fitness trainer profile, you connect with clients directly and keep 100% of your earnings."
          }
        }
      ]
    }
  ],
};

const STATIC_FAQS = [
  {
    q: "How do I create my free fitness trainer profile on MeraGym?",
    a: "Creating your free fitness trainer profile is simple! Just fill in your name, email, and experience, verify with an OTP, and your professional profile is live in under 2 minutes."
  },
  {
    q: "Is it really free to create a fitness trainer profile?",
    a: "Absolutely. It is 100% free to create your fitness trainer profile on MeraGym. We believe in helping trainers grow without any upfront registration or listing costs."
  },
  {
    q: "What details are required to create my free fitness trainer profile?",
    a: "To create your free fitness trainer profile, you only need basic info: name, email, phone, city, and experience. Once created, you can add certifications and success stories."
  },
  {
    q: "Can I list multiple specialties when I create my free fitness trainer profile?",
    a: "Yes! When you create your free fitness trainer profile, you can select various specialties like CrossFit, Yoga, Weight Training, or Nutrition to attract more clients."
  },
  {
    q: "How does creating a free fitness trainer profile help me get clients?",
    a: "When you create your free fitness trainer profile, you get indexed on Google and listed on MeraGym, allowing thousands of potential clients to find and contact you directly."
  },
  {
    q: "Do I need to pay a commission after I create my free fitness trainer profile?",
    a: "No. Unlike other platforms, once you create your free fitness trainer profile on MeraGym, we never take a cut from your earnings. You deal with clients directly."
  },
  {
    q: "Is my profile SEO-optimized when I create my free fitness trainer profile?",
    a: "Yes, our platform is built for SEO. Every time a trainer chooses to create their free fitness trainer profile here, we optimize it to boost visibility in local searches."
  },
  {
    q: "Can I add my certifications after I create my free fitness trainer profile?",
    a: "Yes, you can easily upload your certificates and awards to your dashboard after you create your free fitness trainer profile to increase your credibility and trust."
  },
  {
    q: "How long does it take to create a free fitness trainer profile on MeraGym?",
    a: "The entire process to create your free fitness trainer profile takes less than 120 seconds. It's designed to be fast, easy, and mobile-friendly."
  },
  {
    q: "Why should I choose MeraGym to create my free fitness trainer profile?",
    a: "MeraGym is India's dedicated fitness platform. When you create your free fitness trainer profile here, you join a trusted community with zero commission and high local reach."
  }
];

export default function TrainerRegister() {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const [step,           setStep]           = useState(1);
  const [submitting,     setSubmitting]     = useState(false);
  const [errors,         setErrors]         = useState({});
  const [otp,            setOtp]            = useState("");
  const [otpError,       setOtpError]       = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [video,          setVideo]          = useState(null);
  const [openFaq,        setOpenFaq]        = useState(null);

  const [form, setForm] = useState({
    name: "", email: "", countryCode: "+91",
    phone: "", city: "", experience: "", bio: "",
  });

  useEffect(() => {
    axios
      .get(`${config.apiUrl}/page-video?page=create_trainer_profile`)
      .then((res) => { if (res.data.data) setVideo(Array.isArray(res.data.data) ? res.data.data[0] : res.data.data); })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "", general: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Full name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "A valid email address is required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10)
      errs.phone = "Enter a valid 10-digit phone number";
    if (!form.city.trim()) errs.city = "City is required";
    if (form.experience === "") errs.experience = "Please select your experience";
    return errs;
  };

  const handleSubmitDetails = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const checkRes = await axios.post(`${config.apiUrl}/trainers/check-email`, { email: form.email });
      if (checkRes.data.is_registered) {
        setErrors({ email: "This email is already registered. Please login instead." });
        setSubmitting(false);
        return;
      }
      await axios.post(`${config.apiUrl}/trainers/send-otp`, { email: form.email });
      setStep(2);
      startResendCooldown();
    } catch (err) {
      setErrors({ general: err.response?.data?.message || "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await axios.post(`${config.apiUrl}/trainers/send-otp`, { email: form.email });
      startResendCooldown();
    } catch {
      setOtpError("Failed to resend OTP. Please try again.");
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(30);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setOtpError("Please enter the 6-digit OTP"); return; }

    setSubmitting(true);
    try {
      const response = await axios.post(`${config.apiUrl}/trainers/register`, {
        email:        form.email,
        name:         form.name,
        country_code: form.countryCode,
        phone:        form.phone,
        city:         form.city,
        experience:   parseInt(form.experience),
        bio:          form.bio,
        otp,
      });

      if (response.data.success) {
        dispatch(setUser(response.data.data));
        localStorage.setItem("user",  JSON.stringify(response.data.data));
        localStorage.setItem("token", response.data.token);
        setStep(3);
      } else {
        setOtpError(response.data.message || "Registration failed. Please try again.");
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Create Free Fitness Trainer Profile | Register on MeraGym</title>
          <meta
            name="description"
            content="Create your free fitness trainer profile on MeraGym in under 2 minutes. List your services, connect with thousands of clients across India, and grow your personal training business — zero commission, zero setup cost."
          />
          <meta
            name="keywords"
            content="create free fitness trainer profile, create fitness trainer profile, register as fitness trainer, personal trainer registration India, gym trainer profile free, list as trainer meragym, fitness trainer near me, certified trainer India"
          />
          <link rel="canonical" href="https://meragym.com/create-fitness-trainer-profile" />
          <meta property="og:type"        content="website" />
          <meta property="og:title"       content="Create Free Fitness Trainer Profile | MeraGym" />
          <meta property="og:description" content="Create your free fitness trainer profile on MeraGym and start connecting with clients. No commission. No setup cost." />
          <meta property="og:url"         content="https://meragym.com/create-fitness-trainer-profile" />
          <meta name="twitter:card"        content="summary_large_image" />
          <meta name="twitter:title"       content="Create Free Fitness Trainer Profile | MeraGym" />
          <meta name="twitter:description" content="Create your free fitness trainer profile on MeraGym and connect with thousands of clients across India." />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      </HelmetProvider>

      {/* ── Hero + Registration Form ── */}
      <section className="tr-reg-section" aria-label="Create Free Fitness Trainer Profile">
        <div className="tr-reg-bg-shapes" aria-hidden="true">
          <span className="tr-reg-shape tr-reg-shape-1"></span>
          <span className="tr-reg-shape tr-reg-shape-2"></span>
        </div>

        <div className="container">
          <div className="tr-reg-grid">

            {/* ── Left: Pitch Panel ── */}
            <div className="tr-reg-left">
              <nav className="tr-reg-breadcrumb" aria-label="breadcrumb">
                <Link to="/">Home</Link>
                <i className="bi bi-chevron-right" aria-hidden="true"></i>
                <Link to="/gym-fitness-trainer">Fitness Trainers</Link>
                <i className="bi bi-chevron-right" aria-hidden="true"></i>
                <span aria-current="page">Create Fitness Trainer Profile</span>
              </nav>

              <span className="tr-reg-badge">
                <i className="bi bi-patch-check-fill me-1" aria-hidden="true"></i> 100% Free — No Commission
              </span>

              <h1 className="tr-reg-heading">
                Create Your Free{" "}
                <span className="tr-reg-highlight">Fitness Trainer Profile</span>{" "}
                &amp; Grow Your Career
              </h1>
              <p className="tr-reg-subtext">
                Join India's fastest-growing fitness platform. Create your free
                fitness trainer profile today and start connecting with motivated
                clients in your city — no setup cost, no commission ever.
              </p>

              <ul className="tr-reg-benefits" aria-label="Benefits of creating a free trainer profile">
                <li>
                  <span className="tr-benefit-icon" aria-hidden="true"><i className="bi bi-check-circle-fill"></i></span>
                  <div>
                    <strong>Free Profile — Forever</strong>
                    <span>Create and maintain your trainer profile at zero cost</span>
                  </div>
                </li>
                <li>
                  <span className="tr-benefit-icon" aria-hidden="true"><i className="bi bi-check-circle-fill"></i></span>
                  <div>
                    <strong>Zero Commission on Clients</strong>
                    <span>Keep 100% of your earnings — we never take a cut</span>
                  </div>
                </li>
                <li>
                  <span className="tr-benefit-icon" aria-hidden="true"><i className="bi bi-check-circle-fill"></i></span>
                  <div>
                    <strong>Direct Client Enquiries</strong>
                    <span>Clients contact you directly through your profile</span>
                  </div>
                </li>
                <li>
                  <span className="tr-benefit-icon" aria-hidden="true"><i className="bi bi-check-circle-fill"></i></span>
                  <div>
                    <strong>Rank on Google</strong>
                    <span>Your profile gets indexed and found in local searches</span>
                  </div>
                </li>
                <li>
                  <span className="tr-benefit-icon" aria-hidden="true"><i className="bi bi-check-circle-fill"></i></span>
                  <div>
                    <strong>Showcase Your Expertise</strong>
                    <span>Add certifications, specialties, photos, and success stories</span>
                  </div>
                </li>
              </ul>

              <div className="tr-reg-stats-row" aria-label="Platform statistics">
                <div className="tr-reg-stat">
                  <strong>500+</strong><span>Active Trainers</span>
                </div>
                <div className="tr-reg-stat-divider" aria-hidden="true"></div>
                <div className="tr-reg-stat">
                  <strong>10K+</strong><span>Client Connections</span>
                </div>
                <div className="tr-reg-stat-divider" aria-hidden="true"></div>
                <div className="tr-reg-stat">
                  <strong>₹0</strong><span>Setup Cost</span>
                </div>
              </div>

              <p className="tr-reg-login-prompt">
                Already have a profile? <Link to="/login">Login here</Link>
              </p>
            </div>

            {/* ── Right: Form Card ── */}
            <div className="tr-reg-right">
              <div className="tr-reg-card">

                {step < 3 && (
                  <div className="tr-reg-steps-indicator" aria-label="Registration steps">
                    <div className={`tr-step-dot${step >= 1 ? " active" : ""}${step > 1 ? " done" : ""}`}>
                      {step > 1 ? <i className="bi bi-check" aria-hidden="true"></i> : "1"}
                    </div>
                    <div className={`tr-step-line${step > 1 ? " done" : ""}`} aria-hidden="true"></div>
                    <div className={`tr-step-dot${step >= 2 ? " active" : ""}`}>2</div>
                  </div>
                )}

                {/* ── Step 1: Details ── */}
                {step === 1 && (
                  <>
                    <div className="tr-reg-card-header">
                      <h2>Create Your Free Fitness Trainer Profile</h2>
                      <p>Fill in your details — ready in under 2 minutes</p>
                    </div>

                    {errors.general && (
                      <div className="tr-reg-error-banner" role="alert">
                        <i className="bi bi-exclamation-circle me-2" aria-hidden="true"></i>{errors.general}
                      </div>
                    )}

                    <form onSubmit={handleSubmitDetails} noValidate>
                      <div className="tr-reg-field">
                        <label htmlFor="tr-name">Full Name <abbr title="required">*</abbr></label>
                        <input id="tr-name" type="text" name="name" value={form.name}
                          onChange={handleChange} placeholder="e.g. Rahul Sharma"
                          aria-invalid={!!errors.name} className={errors.name ? "tr-input-error" : ""}
                          autoComplete="name" />
                        {errors.name && <span className="tr-field-err" role="alert">{errors.name}</span>}
                      </div>

                      <div className="tr-reg-field">
                        <label htmlFor="tr-email">Email Address <abbr title="required">*</abbr></label>
                        <input id="tr-email" type="email" name="email" value={form.email}
                          onChange={handleChange} placeholder="you@email.com"
                          aria-invalid={!!errors.email} className={errors.email ? "tr-input-error" : ""}
                          autoComplete="email" />
                        {errors.email && <span className="tr-field-err" role="alert">{errors.email}</span>}
                        <small className="tr-field-hint">We'll send a verification code to this email</small>
                      </div>

                      <div className="tr-reg-field">
                        <label htmlFor="tr-phone">Phone Number <abbr title="required">*</abbr></label>
                        <div className="tr-phone-row">
                          <select name="countryCode" value={form.countryCode} onChange={handleChange}
                            className="tr-country-code" aria-label="Country code">
                            {COUNTRIES.map((c) => (
                              <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                            ))}
                          </select>
                          <input id="tr-phone" type="tel" name="phone" value={form.phone}
                            onChange={handleChange} placeholder="10-digit number"
                            aria-invalid={!!errors.phone} className={errors.phone ? "tr-input-error" : ""}
                            autoComplete="tel" />
                        </div>
                        {errors.phone && <span className="tr-field-err" role="alert">{errors.phone}</span>}
                      </div>

                      <div className="tr-reg-row-2">
                        <div className="tr-reg-field">
                          <label htmlFor="tr-city">City <abbr title="required">*</abbr></label>
                          <input id="tr-city" type="text" name="city" value={form.city}
                            onChange={handleChange} placeholder="e.g. Mumbai"
                            aria-invalid={!!errors.city} className={errors.city ? "tr-input-error" : ""}
                            autoComplete="address-level2" />
                          {errors.city && <span className="tr-field-err" role="alert">{errors.city}</span>}
                        </div>
                        <div className="tr-reg-field">
                          <label htmlFor="tr-exp">Experience <abbr title="required">*</abbr></label>
                          <select id="tr-exp" name="experience" value={form.experience} onChange={handleChange}
                            aria-invalid={!!errors.experience} className={errors.experience ? "tr-input-error" : ""}>
                            <option value="">Select years</option>
                            {EXPERIENCE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          {errors.experience && <span className="tr-field-err" role="alert">{errors.experience}</span>}
                        </div>
                      </div>

                      <div className="tr-reg-field">
                        <label htmlFor="tr-bio">
                          Short Bio <span className="tr-optional">(optional)</span>
                        </label>
                        <textarea id="tr-bio" name="bio" value={form.bio} onChange={handleChange}
                          placeholder="Tell clients about your expertise and training style..."
                          rows={3}></textarea>
                      </div>

                      <button type="submit" className="tr-reg-submit-btn" disabled={submitting}>
                        {submitting
                          ? <><span className="tr-spinner" aria-hidden="true"></span> Sending OTP…</>
                          : <>Create Free Profile <i className="bi bi-arrow-right ms-2" aria-hidden="true"></i></>}
                      </button>

                      <p className="tr-reg-terms">
                        By registering, you agree to our{" "}
                        <Link to="/terms-of-use">Terms of Use</Link> and{" "}
                        <Link to="/privacy-policy">Privacy Policy</Link>
                      </p>
                    </form>
                  </>
                )}

                {/* ── Step 2: OTP ── */}
                {step === 2 && (
                  <>
                    <div className="tr-reg-card-header">
                      <div className="tr-otp-icon" aria-hidden="true">
                        <i className="bi bi-envelope-check-fill"></i>
                      </div>
                      <h2>Verify Your Email</h2>
                      <p>We've sent a 6-digit code to <strong>{form.email}</strong></p>
                    </div>

                    <form onSubmit={handleVerifyAndRegister} noValidate>
                      <div className="tr-reg-field">
                        <label htmlFor="tr-otp">Enter OTP <abbr title="required">*</abbr></label>
                        <input id="tr-otp" type="text" inputMode="numeric" value={otp}
                          onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(""); }}
                          placeholder="6-digit code" maxLength={6}
                          className={`tr-otp-input${otpError ? " tr-input-error" : ""}`}
                          autoComplete="one-time-code" aria-invalid={!!otpError} />
                        {otpError && <span className="tr-field-err" role="alert">{otpError}</span>}
                        <div className="tr-resend-row">
                          <span>Didn't receive it?</span>
                          <button type="button" className="tr-resend-btn"
                            onClick={handleResendOtp} disabled={resendCooldown > 0}>
                            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                          </button>
                        </div>
                      </div>

                      <button type="submit" className="tr-reg-submit-btn" disabled={submitting}>
                        {submitting
                          ? <><span className="tr-spinner" aria-hidden="true"></span> Verifying…</>
                          : <>Complete Registration <i className="bi bi-shield-check ms-2" aria-hidden="true"></i></>}
                      </button>

                      <button type="button" className="tr-reg-back-btn"
                        onClick={() => { setStep(1); setOtp(""); setOtpError(""); }}>
                        <i className="bi bi-arrow-left me-2" aria-hidden="true"></i>Change Email
                      </button>
                    </form>
                  </>
                )}

                {/* ── Step 3: Success ── */}
                {step === 3 && (
                  <div className="tr-reg-success">
                    <div className="tr-success-icon" aria-hidden="true">
                      <i className="bi bi-check-circle-fill"></i>
                    </div>
                    <h2>Welcome, {form.name.split(" ")[0]}! Your Profile is Live.</h2>
                    <p>
                      Your free fitness trainer profile has been created on MeraGym.
                      Complete your profile now to start attracting clients.
                    </p>
                    <div className="tr-success-checklist">
                      <div className="tr-success-check done">
                        <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                        <span>Free trainer profile created</span>
                      </div>
                      <div className="tr-success-check done">
                        <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                        <span>Email verified</span>
                      </div>
                      <div className="tr-success-check pending">
                        <i className="bi bi-circle" aria-hidden="true"></i>
                        <span>Add profile photo</span>
                      </div>
                      <div className="tr-success-check pending">
                        <i className="bi bi-circle" aria-hidden="true"></i>
                        <span>Add your services &amp; availability</span>
                      </div>
                    </div>
                    <button className="tr-reg-submit-btn" onClick={() => navigate("/dashboard")}>
                      Go to Dashboard <i className="bi bi-arrow-right ms-2" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="tr-reg-trust-row" aria-label="Trust signals">
                <span><i className="bi bi-shield-fill-check" aria-hidden="true"></i> Secure &amp; Verified</span>
                <span><i className="bi bi-lock-fill" aria-hidden="true"></i> Data Protected</span>
                <span><i className="bi bi-star-fill" aria-hidden="true"></i> 4.9/5 Rated Platform</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How to Create Your Fitness Trainer Profile ── */}
      <section className="tr-reg-how-section" aria-labelledby="how-heading">
        <div className="container">
          <div className="tr-reg-section-header">
            <span className="tr-reg-badge">Quick &amp; Easy</span>
            <h2 id="how-heading" className="tr-reg-section-title">
              How to Create Your Free Fitness Trainer Profile
            </h2>
            <p className="tr-reg-section-sub">
              Get your trainer profile live on MeraGym in 4 simple steps
            </p>
          </div>

          <div className="tr-how-grid">
            <div className="tr-how-step">
              <div className="tr-how-num" aria-hidden="true">1</div>
              <div className="tr-how-icon" aria-hidden="true"><i className="bi bi-person-plus-fill"></i></div>
              <h3>Fill Your Details</h3>
              <p>Enter your name, email, city, and experience to create your free profile in minutes</p>
            </div>
            <div className="tr-how-arrow" aria-hidden="true"><i className="bi bi-chevron-right"></i></div>
            <div className="tr-how-step">
              <div className="tr-how-num" aria-hidden="true">2</div>
              <div className="tr-how-icon" aria-hidden="true"><i className="bi bi-pencil-square"></i></div>
              <h3>Complete Your Profile</h3>
              <p>Add certifications, specialties, photos, and pricing to stand out to clients</p>
            </div>
            <div className="tr-how-arrow" aria-hidden="true"><i className="bi bi-chevron-right"></i></div>
            <div className="tr-how-step">
              <div className="tr-how-num" aria-hidden="true">3</div>
              <div className="tr-how-icon" aria-hidden="true"><i className="bi bi-people-fill"></i></div>
              <h3>Get Discovered</h3>
              <p>Clients find your profile on Google and MeraGym, then send enquiries directly to you</p>
            </div>
            <div className="tr-how-arrow" aria-hidden="true"><i className="bi bi-chevron-right"></i></div>
            <div className="tr-how-step">
              <div className="tr-how-num" aria-hidden="true">4</div>
              <div className="tr-how-icon" aria-hidden="true"><i className="bi bi-graph-up-arrow"></i></div>
              <h3>Grow Your Business</h3>
              <p>Scale with premium plans for more visibility, priority listing, and verified badge</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── YouTube Video (admin-controlled, hidden if empty) ── */}
      {video && (
        <section className="tr-reg-video-section" aria-labelledby="video-heading">
          <div className="container">
            <div className="tr-reg-section-header">
              <span className="tr-reg-badge">Watch &amp; Learn</span>
              <h2 id="video-heading" className="tr-reg-section-title">
                {video.title || "See How to Create Your Fitness Trainer Profile"}
              </h2>
              {video.description && (
                <p className="tr-reg-section-sub">{video.description}</p>
              )}
            </div>
            <div className="tr-video-wrapper">
              <iframe
                src={video.embed_url}
                title={video.title || "Create Free Fitness Trainer Profile on MeraGym"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* ── Why Create a Free Fitness Trainer Profile on MeraGym ── */}
      <section className="tr-reg-why-section" aria-labelledby="why-heading">
        <div className="container">
          <div className="tr-reg-section-header">
            <span className="tr-reg-badge">Why 500+ Trainers Choose MeraGym</span>
            <h2 id="why-heading" className="tr-reg-section-title">
              The Platform Built for Trainers,<br className="d-none d-md-inline" /> Not for Middlemen
            </h2>
            <p className="tr-reg-section-sub">
              Other platforms take 15–30% of every booking. MeraGym takes <strong>₹0 — forever.</strong>
            </p>
          </div>

          <div className="tr-why-grid">
            {[
              {
                icon: "bi-currency-rupee",
                stat: "₹0",
                statLabel: "Commission Always",
                title: "Zero Commission — Forever",
                desc: "Unlike UrbanClap or Cult.fit that charge 15–30% per booking, MeraGym is 100% free. Every rupee your client pays goes directly to you.",
                highlight: true,
              },
              {
                icon: "bi-search",
                stat: "48h",
                statLabel: "Google Indexing",
                title: "Rank on Google Fast",
                desc: "Your SEO-optimised trainer profile gets indexed within 48 hours. Clients searching \"personal trainer near me\" find you — not your competitors.",
              },
              {
                icon: "bi-chat-dots-fill",
                stat: "100%",
                statLabel: "Direct to You",
                title: "Direct Client Enquiries",
                desc: "Clients reach you via WhatsApp and email directly. No platform gatekeeping, no booking middleman — you own every client relationship.",
              },
              {
                icon: "bi-patch-check-fill",
                stat: "3×",
                statLabel: "More Enquiries",
                title: "Verified Trainer Badge",
                desc: "A verified badge builds instant trust and boosts your ranking in MeraGym search. Verified trainers receive 3× more client enquiries on average.",
              },
              {
                icon: "bi-speedometer2",
                stat: "1",
                statLabel: "Unified Dashboard",
                title: "Powerful Free Dashboard",
                desc: "Manage services, availability, certifications, gallery, enquiries, and reviews from one clean mobile-friendly dashboard — all free.",
              },
              {
                icon: "bi-graph-up-arrow",
                stat: "Free",
                statLabel: "Forever to Start",
                title: "Start Free. Scale When Ready.",
                desc: "Create your profile at zero cost and start earning. Upgrade for priority listing and featured placement only when you're ready to scale.",
              },
            ].map((item, i) => (
              <div className={`tr-why-card${item.highlight ? " tr-why-card--highlight" : ""}`} key={i}>
                <div className="tr-why-card-top">
                  <div className="tr-why-icon" aria-hidden="true">
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <div className="tr-why-stat">
                    <strong>{item.stat}</strong>
                    <span>{item.statLabel}</span>
                  </div>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Comparison strip */}
          <div className="tr-why-compare" role="region" aria-label="MeraGym vs other platforms comparison">
            <div className="tr-why-compare-header">
              <h3>MeraGym vs Other Fitness Platforms</h3>
              <p>See exactly why trainers make the switch</p>
            </div>
            <div className="tr-why-compare-table">
              <div className="tr-compare-col tr-compare-feature">
                <div className="tr-compare-head">Feature</div>
                <div>Profile listing</div>
                <div>Commission per booking</div>
                <div>SEO-optimised profile</div>
                <div>Direct client contact</div>
                <div>Profile analytics</div>
                <div>Verified badge</div>
              </div>
              <div className="tr-compare-col tr-compare-mg">
                <div className="tr-compare-head tr-compare-mg-head">MeraGym ✦</div>
                <div><i className="bi bi-check-circle-fill"></i> Free forever</div>
                <div><i className="bi bi-check-circle-fill"></i> ₹0 always</div>
                <div><i className="bi bi-check-circle-fill"></i> Yes, built-in</div>
                <div><i className="bi bi-check-circle-fill"></i> WhatsApp &amp; email</div>
                <div><i className="bi bi-check-circle-fill"></i> Full dashboard</div>
                <div><i className="bi bi-check-circle-fill"></i> Free to earn</div>
              </div>
              <div className="tr-compare-col tr-compare-other">
                <div className="tr-compare-head">Other Platforms</div>
                <div><i className="bi bi-x-circle-fill"></i> Paid plans</div>
                <div><i className="bi bi-x-circle-fill"></i> 15–30% cut</div>
                <div><i className="bi bi-x-circle-fill"></i> No / limited</div>
                <div><i className="bi bi-x-circle-fill"></i> Via platform only</div>
                <div><i className="bi bi-x-circle-fill"></i> Restricted</div>
                <div><i className="bi bi-x-circle-fill"></i> Paid upgrade</div>
              </div>
            </div>
            <div className="tr-why-compare-cta">
              <button
                className="tr-reg-submit-btn tr-compare-cta-btn"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                Create Free Profile — It Takes 2 Minutes
                <i className="bi bi-arrow-up ms-2" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <Faq page="create_trainer_profile" />

      {/* ── Final CTA ── */}
      <section className="tr-reg-final-cta" aria-labelledby="final-cta-heading">
        <div className="container">
          <div className="tr-final-cta-inner">
            <h2 id="final-cta-heading">
              Ready to Create Your Free Fitness Trainer Profile?
            </h2>
            <p>
              Join 500+ certified trainers already growing their business on MeraGym — it takes less than 2 minutes
            </p>
            <button
              className="tr-reg-submit-btn tr-cta-btn"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Create Free Profile Now <i className="bi bi-arrow-up ms-2" aria-hidden="true"></i>
            </button>
            <p className="tr-cta-sub">
              No credit card required &nbsp;·&nbsp; Free forever &nbsp;·&nbsp; Zero commission
            </p>
          </div>
        </div>
      </section>

      {/* ── SEO FAQ Section ── */}
      <section className="tr-seo-faq-section" aria-labelledby="seo-faq-heading">
        <div className="container">
          <div className="tr-reg-section-header">
            <span className="tr-reg-badge">Common Questions</span>
            <h2 id="seo-faq-heading" className="tr-reg-section-title">
              Everything You Need to Know to Create Your Free Fitness Trainer Profile
            </h2>
            <p className="tr-reg-section-sub">
              Quick answers to help you get started and grow your personal training business
            </p>
          </div>

          <div className="tr-seo-faq-accordion">
            {STATIC_FAQS.map((faq, i) => (
              <div
                className={`tr-seo-faq-item${openFaq === i ? " open" : ""}`}
                key={i}
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  className="tr-seo-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`seo-faq-answer-${i}`}
                >
                  <span itemProp="name">{faq.q}</span>
                  <i className={`bi ${openFaq === i ? "bi-dash-circle-fill" : "bi-plus-circle-fill"}`} aria-hidden="true"></i>
                </button>
                <div
                  id={`seo-faq-answer-${i}`}
                  className="tr-seo-faq-answer-wrapper"
                  style={{ maxHeight: openFaq === i ? "400px" : "0" }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p className="tr-seo-faq-answer" itemProp="text">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
